import { isEqual } from 'lodash-es';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { shallow } from 'zustand/shallow';

import { Button, ButtonInner, Chip, Dropdown, Icon, Input, Tooltip, formatNumber } from '@column/column-ui-kit';

import { TransferSnapshotModal } from '../Home/_components/TransferSnapshotModal';
import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import {
  EmptyState,
  PopoverFilterAmount,
  PopoverFilterAmountData,
  PopoverFilterTransferDate,
  PopoverFilterTransferDateData,
  PopoverFilterTransferStatus,
  PopoverFilterTransferType,
  PopoverFilterTransferTypeData,
  PopoverFilterWrapper,
  TableColumn,
} from '~/components';
import { ButtonGroupDropdown } from '~/components/ButtonGroupDropdown';
import { SimulateTransferModal, useDialog } from '~/components/Dialog';
//
import { useBankAccountOptions } from '~/components/fields/BankAccountDropdown';
import { OverviewTable, OverviewTableRefProps } from '~/components/OverviewTable';
import { Search } from '~/components/Search';
import { AbsoluteTime, IconTransfers, RelativeTime } from '~/elements';
import { useQueryParams } from '~/hooks';
import { TransferFilterType, useCheckTransferId } from '~/hooks/useTransfers';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import { useNavigate } from '~/lib/navigation';
import { TransferStatusChip } from '~/lib/transfers';
import { formatTransferType, getTransferIcon, getTransferURL } from '~/lib/transfers/utils';
import { TransferEntryType, TransferListResponse, TransferRepository, transfers } from '~/repositories';
//
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { FormElement, FormLabel, Paragraph } from '~/styles';
import { downloadBlob, getDateFormat } from '~/util';

import { TransferFiltersModal } from './_components/TransferFiltersModal';

export interface TransferLocationState {
  trigger?: 'delayedFetch';
}

export const TransferAmount = styled.div<{ $isAdding?: boolean }>`
  color: ${({ theme, $isAdding }) => ($isAdding === true ? theme.success.background : theme.foreground)};

  ${({ $isAdding }) =>
    $isAdding === false &&
    css`
      position: relative;

      &:before {
        content: '-';
        position: absolute;
        right: 100%;
        display: block;
        top: -1px;
        font: inherit;
        line-height: inherit;
        z-index: 10;
      }
    `}
`;

const Note = styled.div`
  font-size: 12px;
  font-weight: 400;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  flex: auto;
  gap: 8px;
  margin-left: auto;
`;

const Description = styled.div`
  display: flex;
  align-items: baseline;
  grid-gap: 8px;
  padding: 4px 0;
  max-width: 100%;

  ${Note} {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

export const ResetFilter = styled(Button)`
  padding-left: 0;
  padding-right: 0;
`;

export const CreateSnapshot = styled(Button)`
  padding-left: 0;
  padding-right: 0;
`;

const FilterActions = styled.div<{ $isHidden?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: ${({ $isHidden }) => ($isHidden ? 0 : '0 8px')};
  transition: opacity 0.2s;
  width: ${({ $isHidden }) => ($isHidden ? 0 : 'auto')};
  opacity: ${({ $isHidden }) => ($isHidden ? 0 : 1)};
  pointer-events: ${({ $isHidden }) => ($isHidden ? 'none' : 'auto')};
`;

export const FilterLabel = styled.div`
  align-self: center;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary.blendToBackground(700)};
  margin-right: 8px;
`;

const MobileFiltersWrapper = styled.div`
  display: none;
  align-items: center;
  gap: 8px;
  width: 100%;

  @container table-actions (max-width: 740px) {
    display: flex;
  }
`;

const DesktopFiltersWrapper = styled.div`
  display: contents;

  @container table-actions (max-width: 740px) {
    display: none;
  }
`;

const Fields = styled.div`
  margin-top: 12px;
  display: grid;
  grid-gap: 8px;
`;

const FileNameElement = styled(FormElement)`
  margin-top: 12px;
  text-align: left;
`;

const FileTypeLabel = styled.span`
  position: absolute;
  right: 12px;
  top: 8px;
  line-height: 24px;
  pointer-events: none;
  font-size: 14px;
  color: ${({ theme }) => theme.secondary.blendToBackground(800)};
`;

export const FilterDropdown = styled(Dropdown)<{ $isFilterActive?: boolean }>`
  table-layout: fixed;
  width: fit-content;
  max-width: 320px;
  min-width: 98px;

  button {
    --button-scale-pressed: 1;
    padding: 8px 10px 8px 12px;
    width: 100%;

    ${({ theme }) =>
      theme.id !== 'default' &&
      css`
        &::before {
          background-image: none;
        }
      `}

    &::after {
      box-shadow:
        0 -1px 1px rgba(255, 255, 255, 0.75) inset,
        ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
          ${({ theme }) => theme.style.buttonSecondaryBorderWidth}
          ${({ theme }) => theme.style.buttonSecondaryBorderColor},
        0 1px 1px rgba(0, 0, 0, 0.075),
        0 1px 4px 0 transparent inset;

      ${({ theme }) =>
        theme.id !== 'default' &&
        css`
          background-color: transparent;
          box-shadow: inset 0 0 0 1px ${theme.secondary.blendToBackground(100)};

          &:hover {
            background-color: ${({}) => theme.secondary.blendToBackground(25)};
            box-shadow: inset 0 0 0 1px ${theme.secondary.blendToBackground(150)};
          }
        `}

      ${({ $isFilterActive }) =>
        $isFilterActive &&
        css`
          box-shadow:
            ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
              ${({ theme }) => theme.style.buttonSecondaryBorderWidth}
              ${({ theme }) => theme.style.buttonSecondaryBorderColor},
            0 0 0 var(--button-outline, 0px)
              var(--button-outline-color, ${({ theme }) => theme.style.buttonFocussedOutlineColor}),
            ${({ theme }) => theme.style.buttonSecondaryShadow};
        `}
    }

    &:hover {
      &::after {
        box-shadow:
          0 -1px 1px rgba(255, 255, 255, 0.75) inset,
          ${({ theme }) => theme.style.buttonSecondaryHoveredBorderProperties}
            ${({ theme }) => theme.style.buttonSecondaryHoveredBorderWidth}
            ${({ theme }) => theme.secondary.blendToBackground(1100, 150)},
          0 1px 1px rgba(0, 0, 0, 0),
          0 1px 4px 0 ${({ theme }) => theme.secondary.blendToBackground(120)} inset;

        ${({ theme }) =>
          theme.id !== 'default' &&
          css`
            background-color: ${({}) => theme.secondary.blendToBackground(25)};
            box-shadow: inset 0 0 0 1px ${theme.secondary.blendToBackground(150)};
          `}
      }
    }
  }

  ${ButtonInner} {
    transition: 0.2s ease;
    color: ${({ theme, $isFilterActive }) => ($isFilterActive ? theme.primary.background : 'inherit')};
  }

  &:after {
    content: '';
    border-radius: ${({ theme }) => theme.style.buttonSmallBorderRadius};
    z-index: 4;
    position: absolute;
    inset: 0;
    pointer-events: none;
    box-shadow: inset 0 0 0 1px
      ${({ $isFilterActive, theme }) => ($isFilterActive ? theme.primary.background : 'transparent')};
    transition: box-shadow 0.15s;
  }
`;

export const transferTableColumns = (
  selectedTimeOption: string = 'relative',
  onTimeOptionChange?: (value: string) => void
): TableColumn[] => [
  {
    Header: 'Amount / Description',
    accessor: 'description',
    width: 'minmax(300px, auto)',
    Cell: (current) => (
      <Description>
        {current.value[0]}
        {current.value[1] && <Note>{current.value[1]}</Note>}
      </Description>
    ),
  },
  {
    Header: 'Type',
    accessor: 'type',
    width: 'minmax(min-content, 116px)',
    Cell: (current) => (
      <Chip variant="subtle" icon={getTransferIcon(current.value)}>
        {formatTransferType(current.value)}
      </Chip>
    ),
  },
  {
    Header: 'Status',
    accessor: 'status',
    width: 'minmax(min-content, 116px)',
    Cell: (current) => <TransferStatusChip status={current.value} />,
  },
  {
    Header: 'Direction',
    accessor: 'incoming',
    width: 'minmax(min-content, 116px)',
    Cell: (current) => (
      <Chip
        variant="subtle"
        icon={
          current.value === 'Incoming' ? (
            <Icon.CircleArrowDown />
          ) : current.value === 'Outgoing' ? (
            <Icon.CircleArrowUp />
          ) : current.value === 'Deposit' ? (
            <Icon.CircleArrowDown />
          ) : current.value === 'Issue' ? (
            <Icon.CircleArrowUp />
          ) : current.value === 'Internal' ? (
            <Icon.Swap />
          ) : undefined
        }
      >
        {current.value}
      </Chip>
    ),
  },
  {
    accessor: 'created',
    Header: 'Created',
    timeFormat: {
      selectedOption: selectedTimeOption,
      onOptionChange: onTimeOptionChange || (() => undefined),
    },
    sortType: 'datetime',
    width: 'min-content',
    Cell: (current) =>
      selectedTimeOption === 'relative' ? (
        <RelativeTime timestamp={current.value} />
      ) : (
        <AbsoluteTime timestamp={current.value} />
      ),
  },
];

interface PageTransferExportModalContentProps {
  text: string;
}

export const PageTransferExportModalContent: React.FC<PageTransferExportModalContentProps> = ({ text }) => {
  const { getAlertData, setAlertData } = useDialog();

  return (
    <>
      <Paragraph>{text}</Paragraph>
      <Fields>
        <FileNameElement fullWidth>
          <FormLabel>File type</FormLabel>
          <Dropdown
            active="csv"
            variant="muted"
            fullWidth
            options={[
              {
                label: '.csv (Comma-separated values)',
                value: 'csv',
              },
              {
                label: '.json (JavaScript Object Notation)',
                value: 'json',
                isDisabled: true,
              },
              {
                label: '.txt (BAI2 format)',
                value: 'txt',
                isDisabled: true,
              },
            ]}
          />
        </FileNameElement>
        <FileNameElement fullWidth>
          <FormLabel>
            Custom file name <small>(optional)</small>
          </FormLabel>
          <Input
            value={String(getAlertData('export-transfers')?.customFileName ?? '')}
            onChange={(value) => setAlertData('export-transfers', { customFileName: value })}
            placeholder={`transfers-${getDateFormat(new Date())}`}
          >
            <FileTypeLabel>.csv</FileTypeLabel>
          </Input>
        </FileNameElement>
      </Fields>
    </>
  );
};

export const PageTransfers: React.FC = () => {
  const { isAuthorized, isSandbox, currentPermissionName, settings, updateSettings } = useSessionStore();
  const { openAlert, closeAlert, getAlertData, setAlertData } = useDialog();
  const navigate = useNavigate();
  const location = useLocation();
  const { addDangerNotification } = useNotificationStore();
  const overviewTableRef = useRef<OverviewTableRefProps>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isSandboxModalOpen, setIsSandboxModalOpen] = useState<boolean>(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
  const [selectedTimeOption, setSelectedTimeOption] = useState<string>('relative');
  const mountedRef = useRef<boolean>(false);
  const [isBelowTablet, setIsBelowTablet] = useState<boolean>(false);
  const filtersButtonRef = useRef<HTMLButtonElement>(null);

  const { queryParams, getQueryObject } = useQueryParams<TransferFilterType>();

  const locationState = location.state as TransferLocationState;

  const [totalResultsCount, setTotalResultsCount] = useState<number>(0);

  const handleResponse = useCallback((response?: TransferListResponse) => {
    setTotalResultsCount(response?.totalResultsCount ?? 0);
  }, []);

  const [filterByIdTooltipContent, setFilterByIdTooltipContent] = useState<string>('');
  const [filterByIdValue, setFilterByIdValue] = useState<string>('');

  const hasInternationalWires = useFeatureFlag(FeatureFlag.HasInternationalWires, { default: true });

  const timeFormat = settings.tableSettings?.transfers?.timeFormat || 'relative';

  useEffect(() => {
    setSelectedTimeOption(timeFormat);
  }, [timeFormat]);

  //

  const handleTimeOptionChange = useCallback(
    async (value: string) => {
      setSelectedTimeOption(value);
      try {
        await updateSettings({
          tableSettings: {
            transfers: {
              limit: settings.tableSettings?.transfers?.limit || 10,
              timeFormat: value === 'relative' ? TableTimeFormat.Relative : TableTimeFormat.Absolute,
            },
          },
        });
      } catch (error) {
        addDangerNotification({ content: 'Failed to save time format preference' });
      }
    },
    [updateSettings, settings.tableSettings]
  );

  const allowedTransfers = useMemo(() => {
    return transfers
      .filter((v) => v.id && (v.id !== 'intl-wire' || hasInternationalWires))
      .filter((v) => {
        // Hide Check Scanner below tablet breakpoint
        if (v.id === 'check' && isBelowTablet) {
          return false;
        }
        return true;
      })
      .filter(
        (v) =>
          v.permissionName &&
          v.permissionName.some((permission) =>
            isAuthorized({
              permission,
              permissionLevel: 'write',
            })
          )
      );
  }, [isAuthorized, isBelowTablet]);

  useEffect(() => {
    if (!queryParams) {
      return undefined;
    }

    const { counterpartyId, accountNumberId } = queryParams;

    setFilterByIdValue(counterpartyId || accountNumberId || '');
  }, []);

  const handleFilterByIdValueChange = useCallback((value: string) => {
    setFilterByIdValue(value);
    setFilterByIdTooltipContent('');
  }, []);

  const [bankAccountIdFilterData, setBankAccountIdFilterData] = useState<string | undefined>(() => {
    if (!queryParams) return undefined;
    const { bankAccountId } = queryParams;
    return bankAccountId && bankAccountId.length > 0 ? bankAccountId : undefined;
  });

  const {
    options: bankAccountList,
    hiddenOptions: bankAccountHiddenOptions,
    search: searchBankAccounts,
  } = useBankAccountOptions({ value: bankAccountIdFilterData });

  const [amountFilterData, setAmountFilterData] = useState<PopoverFilterAmountData | undefined>(() => {
    if (!queryParams) return undefined;
    const { amount, amountGte, amountLte } = queryParams;
    return {
      amount: amount ? Number(amount) : undefined,
      amountGte: amountGte ? Number(amountGte) : undefined,
      amountLte: amountLte ? Number(amountLte) : undefined,
    };
  });
  const isAmountFilterActive = useMemo(() => {
    if (
      (amountFilterData?.amount && amountFilterData?.amount > 0) ||
      (amountFilterData?.amountGte && amountFilterData?.amountGte > 0) ||
      (amountFilterData?.amountLte && amountFilterData?.amountLte > 0)
    ) {
      return true;
    }
    return false;
  }, [amountFilterData]);

  const [transferTypeFilterData, setTransferTypeFilterData] = useState<PopoverFilterTransferTypeData>(() => {
    if (!queryParams) return {};
    const { isIncoming, type } = queryParams;
    return {
      isIncoming:
        typeof isIncoming !== 'undefined'
          ? typeof isIncoming === 'string'
            ? isIncoming === 'true'
            : isIncoming === true
          : undefined,
      type: type ? (type.split(',') as any) : [],
    };
  });
  const isTransferTypeFilterActive = useMemo(() => {
    if (
      (transferTypeFilterData && transferTypeFilterData.type && transferTypeFilterData.type.length > 0) ||
      (transferTypeFilterData && transferTypeFilterData.isIncoming !== undefined)
    ) {
      return true;
    }
    return false;
  }, [transferTypeFilterData]);

  const [transferStatusFilterData, setTransferStatusFilterData] = useState<string[] | undefined>(() => {
    if (!queryParams) return [];
    const { status } = queryParams;
    return status ? status.split(',') : [];
  });
  //
  //

  const [transferDateFilterData, setTransferDateFilterData] = useState<Partial<PopoverFilterTransferDateData>>(() => {
    if (!queryParams) return {};
    const { createdAtGte, createdAtLt } = queryParams;
    return {
      createdAtGte: createdAtGte && createdAtGte.length > 0 ? createdAtGte : undefined,
      createdAtLt: createdAtLt && createdAtLt.length > 0 ? createdAtLt : undefined,
    };
  });
  const isDateFilterActive = useMemo(() => {
    if (transferDateFilterData?.createdAtGte && transferDateFilterData?.createdAtLt) {
      return true;
    }
    return false;
  }, [transferDateFilterData]);

  const isFilterActive = useMemo(() => {
    return (
      filterByIdValue ||
      bankAccountIdFilterData ||
      isAmountFilterActive ||
      isTransferTypeFilterActive ||
      (transferStatusFilterData && transferStatusFilterData.length > 0) ||
      isDateFilterActive
    );
  }, [
    filterByIdValue,
    bankAccountIdFilterData,
    isAmountFilterActive,
    transferTypeFilterData,
    transferStatusFilterData,
    isDateFilterActive,
  ]);

  useEffect(() => {
    const handleResize = () => {
      if (typeof window === 'undefined') {
        return;
      }
      setIsBelowTablet(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const checkTransferId = useCheckTransferId({
    onSuccess: (id) => {
      const url = transfers.find((transfer) => transfer.idString && id.startsWith(transfer.idString))?.id;
      navigate(`${ROUTE.TRANSFERS}/${url}/${id}`);
    },
    onError: (message) => {
      setFilterByIdTooltipContent(message);
    },
  });

  const handleFilterById = useCallback(
    (value: string) => {
      if (value.length > 0 && transfers.some((transfer) => transfer.idString && value.startsWith(transfer.idString))) {
        checkTransferId(value);

        return;
      }

      if (value.startsWith('cpty_')) {
        overviewTableRef.current?.setQueryParams({
          counterpartyId: value,
          bankAccountId: undefined,
          accountNumberId: undefined,
        });

        return;
      }

      if (value.startsWith('bacc_')) {
        overviewTableRef.current?.setQueryParams({
          bankAccountId: value,
          counterpartyId: undefined,
          accountNumberId: undefined,
        });

        return;
      }

      if (value.startsWith('acno_')) {
        overviewTableRef.current?.setQueryParams({
          accountNumberId: value,
          counterpartyId: undefined,
          bankAccountId: undefined,
        });

        return;
      }

      if (value && value.length > 0) {
        setFilterByIdTooltipContent('Not a supported ID. Enter a Bank Account ID, Counterparty ID, or Transfer ID.');
      } else {
        setTimeout(() => overviewTableRef.current?.reset(), 0);
        setFilterByIdTooltipContent('');
      }
    },
    [overviewTableRef, checkTransferId]
  );

  useLayoutEffect(() => {
    if (locationState?.trigger === 'delayedFetch' && overviewTableRef.current) {
      window.history.replaceState({}, '');
      setTimeout(() => {
        overviewTableRef.current?.fetch();
      }, 300);
    }
  }, [locationState?.trigger, overviewTableRef]);

  const [fetchFilters, setFetchFilters] = useState<TransferFilterType | null>({});

  useEffect(() => {
    const paginationFields = ['page', 'startingAfter', 'endingBefore', 'limit'];
    const filterParams = queryParams
      ? Object.fromEntries(Object.entries(queryParams).filter(([key]) => !paginationFields.includes(key)))
      : {};

    const filterObject: any = {
      ...filterParams,
      ...amountFilterData,
      bankAccountId:
        bankAccountIdFilterData && bankAccountIdFilterData.length > 0 ? bankAccountIdFilterData : undefined,
      ...transferDateFilterData,
      type:
        transferTypeFilterData.type && transferTypeFilterData.type?.length > 0
          ? transferTypeFilterData.type?.join(',')
          : undefined,
      status:
        transferStatusFilterData && transferStatusFilterData?.length > 0
          ? transferStatusFilterData?.join(',')
          : undefined,
    };

    // Always override isIncoming from state; undefined will be filtered out below
    filterObject.isIncoming = transferTypeFilterData.isIncoming;

    const entries = Object.fromEntries(
      Object.entries(filterObject).filter(([, value]) => {
        if (value === undefined || value === null) {
          return false;
        }
        if (typeof value === 'object' && Object.keys(value).length === 0) {
          return false;
        }
        if (Array.isArray(value) && value.length < 1) {
          return false;
        }
        return true;
      })
    );

    setFetchFilters((s) => (isEqual(entries, s) ? s : entries));
  }, [
    queryParams,
    amountFilterData,
    transferTypeFilterData,
    transferStatusFilterData,
    transferDateFilterData,
    bankAccountIdFilterData,
  ]);

  const handleFilterReset = useCallback(() => {
    flushSync(() => {
      setBankAccountIdFilterData('');
      setAmountFilterData({
        amount: undefined,
        amountGte: undefined,
        amountLte: undefined,
      });
      setTransferTypeFilterData({});
      setTransferStatusFilterData([]);
      setTransferDateFilterData({});

      setFilterByIdValue('');
      setFilterByIdTooltipContent('');

      setFetchFilters(null);
    });
  }, []);

  const [exportAnimate, setExportAnimate] = useState<boolean>(false);

  const handleExport = () => {
    if (getAlertData('export-transfers')?.submitLoading) {
      return;
    }

    setAlertData('export-transfers', { submitLoading: true });
    setExportAnimate(true);

    const params = { ...getQueryObject };

    if (params?.page) {
      delete params.page;
    }

    if (params?.limit) {
      delete params.limit;
    }

    if (params?.startingAfter) {
      delete params.startingAfter;
    }

    if (params?.endingBefore) {
      delete params.endingBefore;
    }

    TransferRepository.export(params)
      .then((response) => {
        downloadBlob(
          response,
          `${
            String(getAlertData('export-transfers')?.customFileName).length > 0
              ? getAlertData('export-transfers')?.customFileName
              : `transfers-${getDateFormat(new Date())}`
          }.csv`
        );
      })
      .then(() => {
        closeAlert();
      })
      .catch((e) =>
        addDangerNotification({
          content: e.message,
          display: 'popup',
        })
      )
      .finally(() => {
        setAlertData('export-transfers', { submitLoading: false });
      });
  };

  const handleExportClick = () => {
    let text = `Do you want to export & download the .csv file for ${formatNumber(totalResultsCount, false)} transfers?`;

    if (!isFilterActive) {
      text = `Are you sure you want to export & download the .csv file for all transfers (${formatNumber(
        totalResultsCount,
        false
      )}) without any filters applied?`;
    }

    if (totalResultsCount > 1000) {
      text = `Are you sure to export & download the .csv file for ${formatNumber(
        totalResultsCount,
        false
      )} transfers? This may take a while.`;
    }

    openAlert({
      headline: 'Export',
      content: <PageTransferExportModalContent text={text} />,
      type: 'primary',
      confirmButton: {
        label: 'Download',
        variant: 'primary',
      },
      onSuccess: handleExport,
    });
  };

  //

  useEffect(() => {
    setTimeout(() => {
      mountedRef.current = true;
    }, 2000);
  }, []);

  useEffect(
    () =>
      useSessionStore.subscribe(
        (state) => [state.isSandbox, state.currentUser?.defaultPlatformId],
        () => {
          if (!mountedRef.current) {
            return;
          }

          handleFilterReset();
        },
        {
          equalityFn: shallow,
        }
      ),
    [mountedRef.current]
  );

  const platformRole = useSessionStore((state) => state.currentPermissionName);

  const snapshotFilters = useMemo(() => {
    const newFilters = { ...fetchFilters };

    delete newFilters?.limit;
    delete newFilters?.startingAfter;
    delete newFilters?.endingBefore;
    delete newFilters?.page;

    return newFilters;
  }, [fetchFilters]);

  const columns = transferTableColumns(selectedTimeOption, handleTimeOptionChange);

  return (
    <>
      <TransferSnapshotModal isOpen={isOpen} onClose={() => setIsOpen(false)} filters={snapshotFilters} />

      <SimulateTransferModal
        isOpen={isSandboxModalOpen}
        onClose={() => setIsSandboxModalOpen(false)}
        onSuccess={() => {
          if (overviewTableRef.current) {
            setTimeout(() => overviewTableRef.current?.fetch(), 200);
          }
        }}
      />

      <TransferFiltersModal
        isOpen={isFiltersOpen}
        onClose={() => setIsFiltersOpen(false)}
        bankAccountList={bankAccountList}
        bankAccountHiddenOptions={bankAccountHiddenOptions}
        searchBankAccounts={(description: string) => searchBankAccounts(description)}
        initialBankAccountId={bankAccountIdFilterData}
        initialAmount={amountFilterData}
        initialType={{
          isIncoming: transferTypeFilterData.isIncoming,
          type: transferTypeFilterData.type as unknown as string[],
        }}
        initialStatus={transferStatusFilterData}
        initialDate={{
          createdAtGte: transferDateFilterData?.createdAtGte,
          createdAtLt: transferDateFilterData?.createdAtLt,
        }}
        initialFilterByIdValue={filterByIdValue}
        onFilterById={handleFilterById}
        onApply={(values) => {
          setBankAccountIdFilterData(values.bankAccountId);
          setAmountFilterData({ amount: values.amount, amountGte: values.amountGte, amountLte: values.amountLte });
          setTransferTypeFilterData({
            isIncoming: values.isIncoming,
            type: values.type as unknown as typeof transferTypeFilterData.type,
          });
          setTransferStatusFilterData(values.status);
          setTransferDateFilterData({ createdAtGte: values.createdAtGte, createdAtLt: values.createdAtLt });
        }}
      />
      <PageActions>
        {isSandbox && (
          <Button onClick={() => setIsSandboxModalOpen(true)} icon={<Icon.Sandbox />} size="small">
            Simulate
          </Button>
        )}
        {allowedTransfers.length > 0 && (
          <Tooltip
            content={`With your ${currentPermissionName} role, access here is limited.`}
            isOpen={allowedTransfers.length === 0 ? undefined : false}
            placement="left"
          >
            <ButtonGroupDropdown
              key={hasInternationalWires ? 'intl-wire' : 'wire'}
              buttons={[
                {
                  label: 'Create Transfer',
                  icon: <Icon.Plus />,
                  isDisabled: allowedTransfers.length === 0,
                  onClick: () =>
                    navigate(`${ROUTE.TRANSFERS}/edit/${platformRole === 'Check Depositor' ? 'check' : 'ach'}`),
                },
              ]}
              options={allowedTransfers.map((transfer) => {
                const IconElement = IconTransfers[transfer.icon];
                return {
                  icon: <IconElement />,
                  label: transfer.label,
                  onClick: () => {
                    navigate(`${ROUTE.TRANSFERS}/edit/${transfer.id}`);
                  },
                };
              })}
            />
          </Tooltip>
        )}
      </PageActions>

      <OverviewTable
        tableId="transfers"
        ref={overviewTableRef}
        fetchFilter={fetchFilters}
        columns={columns}
        onResponse={handleResponse}
        onLimitChange={() => handleFilterReset()}
        empty={
          <EmptyState headline="No transfers found" text="Add your first ACH, Wire or Book Transfer to get started.">
            <Button onClick={() => navigate(`${ROUTE.TRANSFERS}/edit/ach`)} size="small" icon={<Icon.Plus />}>
              ACH
            </Button>
            <Button onClick={() => navigate(`${ROUTE.TRANSFERS}/edit/wire`)} size="small" icon={<Icon.Plus />}>
              Wire
            </Button>
            <Button onClick={() => navigate(`${ROUTE.TRANSFERS}/edit/intl-wire`)} size="small" icon={<Icon.Plus />}>
              International Wire
            </Button>
            <Button onClick={() => navigate(`${ROUTE.TRANSFERS}/edit/book`)} size="small" icon={<Icon.Plus />}>
              Book
            </Button>
            <Button onClick={() => navigate(`${ROUTE.TRANSFERS}/edit/issue-check`)} size="small" icon={<Icon.Plus />}>
              Check
            </Button>
            <Button onClick={() => navigate(`${ROUTE.TRANSFERS}/edit/realtime`)} size="small" icon={<Icon.Plus />}>
              Realtime Transfer
            </Button>
          </EmptyState>
        }
        action={
          <>
            <MobileFiltersWrapper>
              <Button ref={filtersButtonRef} size="small" variant="secondary" onClick={() => setIsFiltersOpen(true)}>
                <Icon.Filter />
                Filters
              </Button>

              {isFilterActive && (
                <FilterActions $isHidden={!isFilterActive}>
                  <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
                    Reset
                  </ResetFilter>
                  <CreateSnapshot
                    size="small"
                    variant="subtle-primary"
                    icon={<Icon.Plus />}
                    onClick={() => setIsOpen(true)}
                  >
                    New Snapshot
                  </CreateSnapshot>
                </FilterActions>
              )}
            </MobileFiltersWrapper>
            <DesktopFiltersWrapper>
              <FilterDropdown
                options={bankAccountList}
                hiddenOptions={bankAccountHiddenOptions}
                size="small"
                active={bankAccountIdFilterData}
                $isFilterActive={!!bankAccountIdFilterData}
                label={bankAccountHiddenOptions?.find((b) => b.value === bankAccountIdFilterData)?.label || 'Account'}
                search
                fullWidth
                searchLabel="Search accounts"
                onSearchChange={(description: string) => searchBankAccounts(description)}
                onChange={(value) => {
                  setBankAccountIdFilterData(value);
                  if (!value) {
                    setTimeout(() => {
                      overviewTableRef.current?.setQueryParams({
                        bankAccountId: undefined,
                      });
                    }, 0);
                  }
                }}
              />
              <PopoverFilterWrapper
                label="Amount"
                onSubmit={(data) => {
                  setAmountFilterData(data);
                  if (!data.amount && !data.amountGte && !data.amountLte) {
                    setTimeout(() => {
                      overviewTableRef.current?.setQueryParams({
                        amount: undefined,
                        amountGte: undefined,
                        amountLte: undefined,
                      });
                    }, 0);
                  }
                }}
                isActive={isAmountFilterActive}
              >
                <PopoverFilterAmount data={amountFilterData ?? {}} />
              </PopoverFilterWrapper>
              <PopoverFilterWrapper
                label="Type"
                onSubmit={(data) => {
                  setTransferTypeFilterData(data);
                  if ((!data.type || data.type.length === 0) && typeof data.isIncoming === 'undefined') {
                    setTimeout(() => {
                      overviewTableRef.current?.setQueryParams({
                        type: undefined,
                        isIncoming: undefined,
                      });
                    }, 0);
                  }
                }}
                isActive={isTransferTypeFilterActive}
              >
                <PopoverFilterTransferType data={transferTypeFilterData ?? {}} />
              </PopoverFilterWrapper>
              <PopoverFilterWrapper
                label="Status"
                onSubmit={(data) => {
                  setTransferStatusFilterData(data);
                  if (!data || data.length === 0) {
                    setTimeout(() => {
                      overviewTableRef.current?.setQueryParams({
                        status: undefined,
                      });
                    }, 0);
                  }
                }}
                isActive={transferStatusFilterData && transferStatusFilterData.length > 0}
              >
                <PopoverFilterTransferStatus data={transferStatusFilterData ?? []} />
              </PopoverFilterWrapper>
              <PopoverFilterWrapper
                label="Date"
                onSubmit={(data) => {
                  setTransferDateFilterData(data);
                  if (!data.createdAtGte && !data.createdAtLt) {
                    setTimeout(() => {
                      overviewTableRef.current?.setQueryParams({
                        createdAtGte: undefined,
                        createdAtLt: undefined,
                      });
                    }, 0);
                  }
                }}
                isActive={isDateFilterActive}
              >
                <PopoverFilterTransferDate data={transferDateFilterData ?? {}} />
              </PopoverFilterWrapper>
              <Search
                value={filterByIdValue}
                onFilterSubmit={handleFilterById}
                placeholder="Filter by ID"
                tooltipContent={filterByIdTooltipContent}
                onValueChange={handleFilterByIdValueChange}
              />

              {isFilterActive && (
                <FilterActions $isHidden={!isFilterActive}>
                  <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
                    Reset
                  </ResetFilter>
                  <CreateSnapshot
                    size="small"
                    variant="subtle-primary"
                    icon={<Icon.Plus />}
                    onClick={() => setIsOpen(true)}
                  >
                    New Snapshot
                  </CreateSnapshot>
                </FilterActions>
              )}
            </DesktopFiltersWrapper>

            <Buttons>
              <Button
                icon={<Icon.AnimationDownload running={exportAnimate} onDone={() => setExportAnimate(false)} />}
                size="small"
                variant="secondary"
                onClick={handleExportClick}
              >
                Export
              </Button>
            </Buttons>
          </>
        }
        rowClick={(row) => {
          const transferId = row.original.id;
          const transferType = row.original.type as TransferEntryType;
          navigate(getTransferURL(transferId, transferType));
        }}
      />
    </>
  );
};
